import React from 'react';
import { Badge, Notice, Text } from '@heartinternet/hi-ui';
import type { SSLInfoContext } from '../SSLInfoPage';
import type { OvEvValidations, SSLCheckData, SSLInfoData } from '../types';
import { getCertificateName, getStatusBadgeVariant, getStatusName } from '../helpers';
import { CertificateDetails } from './CertificateDetails';
import { DomainOwnership } from './DomainOwnership';
import { Validation } from './Validation';
import { CertificateActions } from './CertificateActions';

export interface ManageViewProps {
  info: SSLInfoData;
  checkData: SSLCheckData | null;
  ctx: SSLInfoContext;
}

/** Every OV/EV section is VALIDATED or not-applicable (null) — mirrors checkOVEVValidation. */
function allValidated(v: OvEvValidations): boolean {
  return (Object.values(v) as (string | null)[]).every((s) => s === 'VALIDATED' || s === null);
}

/** Derived routing state — mirrors page-submitted.js resolveData(). */
function resolveState(checkData: SSLCheckData | null): {
  isOwned: boolean;
  isValidated: boolean;
  isNearlyComplete: boolean;
  isError: boolean;
} {
  if (!checkData || checkData.status === 'COMPLETED') {
    return { isOwned: true, isValidated: true, isNearlyComplete: false, isError: false };
  }
  const isError = checkData.status === 'ERROR';
  if (checkData.dcv?.status === 'FINALISING') {
    return { isOwned: false, isValidated: false, isNearlyComplete: true, isError };
  }
  return {
    isOwned: checkData.dcv?.status === 'VALIDATED',
    isValidated: allValidated(checkData.ov_ev_validations),
    isNearlyComplete: false,
    isError,
  };
}

function introText(s: {
  isOwned: boolean;
  isValidated: boolean;
  isNearlyComplete: boolean;
}): string {
  if (s.isValidated && s.isOwned)
    return 'Your certificate is active. View the details, download your certificate, or revoke it if you need to.';
  if (s.isOwned && !s.isValidated)
    return 'Your certificate is pending individual/organisation validation. Please complete the following steps to activate your certificate.';
  if (!s.isOwned && s.isValidated)
    return 'Your certificate is pending domain ownership. Please verify ownership through one of the available options to activate your certificate.';
  if (s.isNearlyComplete) return 'Your certificate is nearly ready.';
  return 'Your certificate is pending domain ownership and individual/organisation validation. Please complete the following two sections to activate your certificate.';
}

/** Manage/Submitted flow (status ≠ NEW) — mirrors submitted/page-submitted.js. */
export const ManageView: React.FC<ManageViewProps> = ({ info, checkData, ctx }) => {
  const productName = getCertificateName(info.product_type, info.is_wildcard);
  const state = resolveState(checkData);
  const isActive = state.isOwned && state.isValidated;

  return (
    <div className="hi-ui-ssl-info__flow">
      <div className="hi-ui-ssl-info__manage-header">
        <Text variant="heading-2">Manage {productName} SSL</Text>
        <Badge variant={getStatusBadgeVariant(info.status)}>{getStatusName(info.status)}</Badge>
        {info.common_name && <Text variant="body">{info.common_name}</Text>}
      </div>

      <Text variant="body">{introText(state)}</Text>

      {state.isError ? (
        <Notice variant="error">
          An error occurred when checking validation status. Please try again or contact support.
        </Notice>
      ) : (
        <>
          {!state.isOwned && checkData && (
            <DomainOwnership info={info} dcv={checkData.dcv} ctx={ctx} />
          )}
          {!state.isValidated && !state.isNearlyComplete && checkData && (
            <Validation validations={checkData.ov_ev_validations} ctx={ctx} />
          )}
        </>
      )}

      <CertificateDetails info={info} />

      {isActive && <CertificateActions info={info} ctx={ctx} />}
    </div>
  );
};
